import React, { useState, useEffect } from 'react';
import {
  Bell,
  Megaphone,
  AlertTriangle,
  Info,
  CheckCircle,
  Calendar,
  Clock,
  User,
  Pin,
  ExternalLink,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';
import { useNotifications, useNotificationReadStatus } from '../hooks/useNotifications';

const NotificationsAnnouncements = ({ currentUser }) => {
  const [selectedTab, setSelectedTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Fetch notifications and announcements from Google Sheets
  const { data, loading, error, refresh, lastRefresh } = useNotifications(currentUser);
  const { notifications = [], announcements = [] } = data || {};

  // Manage read status locally
  const { getReadItems, markAsRead, markAsUnread } = useNotificationReadStatus(currentUser);
  const [readItems, setReadItems] = useState(getReadItems());

  // Update read items when user changes
  useEffect(() => {
    setReadItems(getReadItems());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const toggleRead = (id) => {
    if (readItems.has(id)) {
      const newReadItems = markAsUnread(id);
      setReadItems(newReadItems);
    } else {
      const newReadItems = markAsRead([id]);
      setReadItems(newReadItems);
    }
  };

  const markAllAsRead = () => {
    const allIds = getFilteredItems().map(item => item.id);
    const newReadItems = markAsRead(allIds);
    setReadItems(newReadItems);
  };

  // Filter items based on user role and current filters
  const getFilteredItems = () => {
    let allItems = [];
    
    if (selectedTab === 'all' || selectedTab === 'notifications') {
      allItems = [...allItems, ...notifications];
    }
    
    if (selectedTab === 'all' || selectedTab === 'announcements') {
      allItems = [...allItems, ...announcements];
    }

    // Filter by type
    if (filterType !== 'all') {
      allItems = allItems.filter(item => item.type === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      allItems = allItems.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort: pinned first, then by timestamp (newest first)
    allItems.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    return allItems;
  };

  const filteredItems = getFilteredItems();
  const unreadCount = filteredItems.filter(item => !readItems.has(item.id)).length;

  const getTypeIcon = (type) => {
    switch (type) {
      case 'urgent': return AlertTriangle;
      case 'warning': return AlertTriangle;
      case 'info': return Info;
      case 'success': return CheckCircle;
      default: return Bell;
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Unknown date';
      
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 1) {
        const minutes = Math.floor(diffInHours * 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      } else if (diffInHours < 24) {
        const hours = Math.floor(diffInHours);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      } else {
        const days = Math.floor(diffInHours / 24);
        if (days < 7) {
          return `${days} day${days !== 1 ? 's' : ''} ago`;
        } else {
          return date.toLocaleDateString();
        }
      }
    } catch (error) {
      return 'Unknown date';
    }
  };

  // CSS-variable border color per notification type
  const getTypeBorderColor = (type) => {
    switch (type) {
      case 'urgent': return 'var(--status-error)';
      case 'warning': return 'var(--gold-500,#c8a03c)';
      case 'success': return 'var(--status-success,#22c55e)';
      default: return 'var(--forest-400,#4a8c6e)';
    }
  };

  // CSS-variable icon bg per type
  const getTypeIconBg = (type) => {
    switch (type) {
      case 'urgent': return 'rgba(239,68,68,0.1)';
      case 'warning': return 'rgba(234,179,8,0.12)';
      case 'success': return 'rgba(34,197,94,0.12)';
      default: return 'var(--forest-100,#d4e8dd)';
    }
  };

  const getTypeIconColor = (type) => {
    switch (type) {
      case 'urgent': return 'var(--status-error)';
      case 'warning': return 'var(--gold-600,#a07020)';
      case 'success': return 'var(--status-success,#22c55e)';
      default: return 'var(--forest-600,#2d6a50)';
    }
  };

  if (loading) {
    return (
      <div className="k-page">
        <div className="k-card k-loading">
          <div className="k-loading-spinner" />
          <p>Loading notifications…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-page">
        <div className="k-card k-error" style={{ textAlign: 'center', padding: '2rem' }}>
          <AlertTriangle size={36} style={{ margin: '0 auto 12px', color: 'var(--status-error)' }} />
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Error Loading Notifications</p>
          <p style={{ marginBottom: 16 }}>{error}</p>
          <button className="k-btn-primary" onClick={refresh}>
            <RefreshCw size={14} style={{ display: 'inline', marginRight: 6 }} />
            Retry
          </button>
          <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--forest-100,#d4e8dd)', borderRadius: 8, textAlign: 'left' }}>
            <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Setup Required:</p>
            <ul style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              <li>• Configure REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_NOTIFICATIONS</li>
              <li>• Create "Notifications" and "Announcements" sheets</li>
              <li>• Add column headers as specified in documentation</li>
              <li>• Share spreadsheet with service account</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="k-page">

      {/* Refresh bar */}
      <div className="k-refresh-bar">
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : 'Notifications & Announcements'}
          {unreadCount > 0 && <span style={{ marginLeft: 8, color: 'var(--gold-600,#a07020)', fontWeight: 600 }}>• {unreadCount} unread</span>}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {unreadCount > 0 && (
            <button className="k-btn-gold" style={{ fontSize: 12, padding: '4px 12px' }} onClick={markAllAsRead}>
              Mark All Read
            </button>
          )}
          <button className="k-btn-ghost" onClick={refresh} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs + filters */}
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="k-tabs-wrap" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <div className="k-tabs-main" style={{ padding: '0 20px' }}>
            {[
              { key: 'all', label: 'All', count: notifications.length + announcements.length },
              { key: 'notifications', label: 'Notifications', count: notifications.length },
              { key: 'announcements', label: 'Announcements', count: announcements.length }
            ].map(tab => (
              <button
                key={tab.key}
                className={`k-tab-btn${selectedTab === tab.key ? ' active' : ''}`}
                onClick={() => setSelectedTab(tab.key)}
              >
                {tab.label}
                <span className="k-tab-count">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="k-filter-bar" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-2)' }}>
          <input
            type="text"
            className="k-search"
            placeholder="Search notifications…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, maxWidth: 360 }}
          />
          <select
            className="k-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="urgent">Urgent</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
            <option value="success">Success</option>
          </select>
        </div>
      </div>

      {/* Items */}
      {filteredItems.length === 0 ? (
        <div className="k-card k-empty">
          <Bell size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontWeight: 600 }}>No Items Found</p>
          <p>{searchTerm ? 'No notifications match your search' : 'No notifications or announcements available'}</p>
          {!searchTerm && notifications.length === 0 && announcements.length === 0 && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--forest-100,#d4e8dd)', borderRadius: 8, textAlign: 'left', maxWidth: 360 }}>
              <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>To populate this view:</p>
              <ul style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                <li>• Add data to your Google Sheets</li>
                <li>• Target role: {currentUser.role}</li>
                <li>• Mark items as "Active"</li>
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredItems.map((item) => {
            const TypeIcon = getTypeIcon(item.type);
            const isRead = readItems.has(item.id);

            return (
              <div
                key={item.id}
                className="k-card"
                style={{
                  opacity: isRead ? 0.72 : 1,
                  borderLeft: `3px solid ${getTypeBorderColor(item.type)}`,
                  padding: 0, overflow: 'hidden'
                }}
              >
                <div style={{ padding: '16px 20px' }}>
                  {/* Header row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                        background: getTypeIconBg(item.type),
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <TypeIcon size={17} style={{ color: getTypeIconColor(item.type) }} />
                      </div>
                      {item.isPinned && <Pin size={14} style={{ color: 'var(--gold-500,#c8a03c)' }} />}
                      <span className={`k-badge ${item.type === 'urgent' ? 'k-badge-urgent' : item.type === 'warning' ? 'k-badge-medium' : item.type === 'success' ? 'k-badge-low' : 'k-badge-role'}`}>
                        {item.type.toUpperCase()}
                      </span>
                      <span className="k-badge k-badge-role">
                        {item.itemType === 'notification' ? <Bell size={10} style={{ marginRight: 3 }} /> : <Megaphone size={10} style={{ marginRight: 3 }} />}
                        {item.itemType}
                      </span>
                      {item.category && (
                        <span className="k-badge" style={{ background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>
                          {item.category}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => toggleRead(item.id)}
                      className="k-btn-ghost"
                      style={{ padding: '4px 8px', flexShrink: 0 }}
                      title={isRead ? 'Mark as unread' : 'Mark as read'}
                    >
                      {isRead ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>

                  {/* Title */}
                  <p style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', marginBottom: 6 }}>
                    {item.title}
                  </p>

                  {/* Message */}
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10 }}>
                    {item.message}
                  </p>

                  {/* Attachments */}
                  {item.attachments && item.attachments.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <p className="k-field-label" style={{ marginBottom: 4 }}>Attachments</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {item.attachments.map((attachment, idx) => (
                          <a
                            key={idx}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: 'var(--gold-600,#a07020)', display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            <ExternalLink size={11} />
                            {attachment.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action button */}
                  {item.actionRequired && item.actionText && item.actionLink && (
                    <div style={{ marginBottom: 10 }}>
                      <a
                        href={item.actionLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={item.type === 'urgent' ? 'k-btn-primary' : 'k-btn-gold'}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                      >
                        {item.actionText}
                        <ExternalLink size={11} />
                      </a>
                    </div>
                  )}

                  {/* Footer meta */}
                  <div className="k-task-meta">
                    <span className="k-task-meta-item"><User size={11} /> {item.author}</span>
                    <span className="k-task-meta-item"><Clock size={11} /> {formatDate(item.timestamp)}</span>
                    {item.expiresAt && (
                      <span className="k-task-meta-item" style={{ color: 'var(--status-warning,#f97316)' }}>
                        <Calendar size={11} /> Expires: {new Date(item.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredItems.length > 0 && (
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', paddingTop: 8 }}>
          Showing {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
          {unreadCount > 0 && ` • ${unreadCount} unread`}
        </p>
      )}
    </div>
  );
};

export default NotificationsAnnouncements;
